import type { MaybeRef, MaybeRefOrGetter } from "vue"
import type { ServerResponseV1 } from "@/http"
import type { IMediaResponseV1 } from "@/types"
import * as Sentry from "@sentry/vue"
import { useIntervalFn } from "@vueuse/core"
import { ref, toValue, watchEffect } from "vue"
import { v1 } from "@/axios"
import { Media, MediaType } from "@/models"
import { useEventVaultStore } from "@/stores/eventVaultStore"
import { usePagination } from "./usePagination"

export interface useSlideshowOptions {
  speed?: MaybeRefOrGetter<number>
  videoElementId?: string
}

export type SlideshowState = "IDLE" | "TITLE" | "PLAYING" | "PAUSED" | "ENDED"

const state = ref<SlideshowState>("IDLE")
const visible = ref<boolean>(false)
const slideshowInterval = ref(5000)
const slideshowMedia = ref<Media[]>([])
const initialised = ref(false)
const isInitialising = ref(false)
const pendingSlideChange = ref(false)
const isLive = ref(false)

const mediaCount = ref(1)

const pagination = usePagination({
  initialPerPage: 1,
  pageCount: mediaCount,
  loop: true,
})

const TRANSITION_SPEED = 750

export function useSlideshow(id?: MaybeRefOrGetter<string>, { speed, videoElementId = "gpx-slideshow-video" }: useSlideshowOptions = {}) {
  const eventVaultStore = useEventVaultStore()

  const initialise = async () => {
    isInitialising.value = true

    try {
      const response = await v1.get<ServerResponseV1<IMediaResponseV1[]>>(`/${eventVaultStore.eventVaultId}/slideshow/${toValue(id)}/start`)

      slideshowMedia.value = response.data.data?.map(media => Media.fromResponseV1(media)) ?? []
      initialised.value = true
    }
    catch (e: unknown) {
      Sentry.withScope((scope) => {
        scope.setTag("area", "slideshow")
        scope.captureException(e)
      })

      throw e
    }
    finally {
      isInitialising.value = false
    }
  }

  const fetchMore = async () => {
    if (!isLive.value) {
      throw new Error("Slideshow is not in live mode, unable to call `fetchMore`")
    }

    if (!initialised.value) {
      throw new Error("Unable to fetch more media, slideshow media should be initialised before calling `fetchMore`")
    }

    try {
      const response = await v1.get<ServerResponseV1<IMediaResponseV1[]>>(`/${eventVaultStore.eventVaultId}/slideshow/${toValue(id)}/start?live=true`)
      const responseMedia = response.data.data?.map(media => Media.fromResponseV1(media)) ?? []

      slideshowMedia.value = [...slideshowMedia.value, ...responseMedia]
    }
    catch (e: unknown) {
      Sentry.withScope((scope) => {
        scope.setTag("area", "slideshow")
        scope.captureException(e)
      })

      throw e
    }
  }

  const { pause: pauseLive, resume: resumeLive } = useIntervalFn(async () => await fetchMore(), slideshowInterval.value)

  watchEffect(() => {
    if (isLive.value && toValue(id)) {
      resumeLive()
    }
    else {
      pauseLive()
    }
  })

  watchEffect(() => {
    mediaCount.value = slideshowMedia.value?.length ?? 1
  })

  watchEffect(() => {
    if (speed) {
      slideshowInterval.value = toValue(speed) + TRANSITION_SPEED
    }
  })

  const show = (index?: MaybeRef<number>) => {
    const indexValue = !Number.isNaN(Number(index)) ? toValue(index) : undefined

    if (indexValue) {
      pagination.page.value = indexValue ?? 1
    }

    visible.value = true
  }

  const hide = () => {
    visible.value = false
  }

  const toggle = () => (visible.value = !visible.value)

  const getVideoElement = (): HTMLVideoElement | undefined => {
    const currentMedia = slideshowMedia.value?.[pagination.page.value - 1]

    if (currentMedia?.type !== MediaType.Vid) {
      return
    }

    const elementId = `${videoElementId}-${currentMedia?.id ?? "unknown"}`

    const element = document.getElementById(elementId)

    return element ? (element as HTMLVideoElement) : undefined
  }

  const { pause: pauseSlideshow, resume: resumeSlideshow } = useIntervalFn(
    // eslint-disable-next-line ts/no-use-before-define
    () => checkCurrentMediaAndProceed(),
    slideshowInterval,
    {
      immediate: false,
    },
  )

  // Function to handle when the current media ends
  const handleMediaEnd = () => {
    if (pagination.canNext.value) {
      pagination.next()
    }
    else {
      pagination.page.value = 1
    }

    pendingSlideChange.value = false
  }

  // check the current media and proceed accordingly
  const checkCurrentMediaAndProceed = () => {
    const videoElement = getVideoElement()

    // if the current media is not a video, videoElement will be undefined
    if (!videoElement) {
      handleMediaEnd()
      return
    }

    const videoDuration = videoElement.duration

    if (videoDuration && videoDuration < slideshowInterval.value / 1000) {
      handleMediaEnd()
      return
    }

    if (!pendingSlideChange.value) {
      videoElement.addEventListener(
        "ended",
        () => {
          handleMediaEnd()
          videoElement.currentTime = 0
          videoElement.pause()
        },
        { once: true },
      )
      pendingSlideChange.value = true
    }
  }

  const play = async () => {
    state.value = "PLAYING"
    resumeSlideshow()

    const videoElement = getVideoElement()

    if (videoElement?.paused) {
      try {
        await videoElement.play()
      }
      catch {
        // Only skip ahead if this is still the current slide's video - avoids
        // double-advancing if the component's own play-on-mount watcher (for
        // the same element) already handled the failure.
        if (getVideoElement() === videoElement)
          handleMediaEnd()
      }
    }
  }

  const pause = () => {
    const videoElement = getVideoElement()

    if (!videoElement?.paused) {
      videoElement?.pause()
    }

    pauseSlideshow()
    state.value = "PAUSED"
  }

  const setIsLive = (value: boolean) => {
    isLive.value = value
  }

  const reset = () => {
    pauseLive()
    state.value = "IDLE"
    initialised.value = false
    visible.value = false
    isLive.value = false
    slideshowInterval.value = 5000
    pendingSlideChange.value = false
    pagination.page.value = 1
    slideshowMedia.value = []
  }

  watchEffect(() => {
    if (!toValue(id)) {
      reset()
    }
  })

  return {
    show,
    hide,
    toggle,
    visible,
    reset,
    state,
    play,
    pause,
    media: slideshowMedia,
    currentInterval: slideshowInterval,
    pagination,
    initialise,
    isInitialising,
    initialised,
    fetchMore,
    setIsLive,
    isLive,
    handleMediaEnd,
  }
}
